import axios from "axios";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "ticketino-api-client";
import { GetShopBasketOrder, GetTicketTypeById, GetTicketTypeInfosByTicketTypeId, ChangePaymentType, ChangeDeliveryMethod, ApplyPromotionCode, deleteTicketsFromBasket } from "../utils/api";
import { useLanguage } from "../hooks/useLanguage";
import NON_DELETABLE_TICKET_BY_EXTERNALCODES from "../utils/non-deletable-ticket-by-externalcodes";

import Header from "./components/Header";
import Footer from "./components/Footer";
import OrderSummary from "./components/OrderSummary";

import "../css/checkout.css";

const Checkout = () => {
    const [regularToken, setRegularToken] = useState(null);
    const [bookingType, setBookingType] = useState(null);
    const [resources, setResources] = useState({});

    const [orderId] = useState(localStorage.getItem("OrderId"));
    const [order, setOrder] = useState({});

    const [tickets, setTickets] = useState([]);
    const [payment, setPayment] = useState({
        paymentMethodId: 0,
        deliveryMethodId: 0,
        insurance: false
    })

    const [promotioncode, setPromotioncode] = useState("");
    const [addedPromotionCodes, setAddedPromotionCodes] = useState([]);
    const [promotionError, setPromotionError] = useState({
        text: "",
        valid: false
    });

    const [orderInsurancePrice, setOrderInsurancePrice] = useState(0);

    const [isAgbChecked, setIsAgbChecked] = useState(false);
    const [isAgb2Checked, setIsAgb2Checked] = useState(false);

    const [submitError, setSubmitError] = useState("");

    const isButtonFinishOrderDisabled = payment.paymentMethodId === 0 || payment.deliveryMethodId === 0 || !isAgbChecked || !isAgb2Checked;

    const { language, languageId } = useLanguage();

    let navigate = useNavigate();
    let baseUrl = process.env.REACT_APP_BASEURL_API;
    api.defaults.baseURL = baseUrl;

    useEffect(() => {
        setRegularToken(localStorage.getItem("token"));
        setBookingType(localStorage.getItem("bookingType"));
    }, []);

    useEffect(() => {
        if (language) {
            requestResources();
        }
    }, [language]);

    useEffect(() => {
        if (regularToken && orderId && orderId.length > 0) {
            loadOrder(orderId);
        }
    }, [regularToken, orderId]);

    useEffect(() => {
        if (order && order?.tickets?.length > 0) {
            loadTickets(order?.tickets)
            getOrderInsurance();
        }
    }, [order]);

    //add promocodes to page if already existing in order
    useEffect(() => {
        if (order && Array.isArray(order.tickets)) {
            const codes = [];
            order.tickets.forEach(ticket => {
                if (ticket.promotionCode && !codes.includes(ticket.promotionCode)) {
                    codes.push(ticket.promotionCode);
                }
            });
            // Only update state if codes are different
            if (codes.length > 0 && (codes.length !== addedPromotionCodes.length || !codes.every(c => addedPromotionCodes.includes(c)))) {
                setAddedPromotionCodes(codes);
            }
        }
    }, [order]);

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.checkOut);
            })
            .catch((error) => console.error(error.response.data));
    };

    const loadOrder = async (orderId) => {
        try {
            const orderResp = await fetchOrderById(orderId);
            let updatedPayment = { ...payment, deliveryMethodId: orderResp.deliveryMethodId, paymentMethodId: orderResp.paymentMethodId };
            updatedPayment.insurance = orderResp.totalInsuranceAmount > 0;

            setPayment(updatedPayment);
            setOrder(orderResp);
        }
        catch (error) {
            console.error(error);
        }
    }

    const loadTickets = async (bookedTickets) => {
        try {
            // Booked Tickets
            const updatedTickets = await Promise.all(
                bookedTickets.map(async (ticket) => {
                    const ticketType = await fetchTicketTypeById(ticket.ticketTypeId);

                    const ticketTypeInfoResp = await fetchtTicketTypeInfosByTicketTypeId(ticket.ticketTypeId);
                    const info = ticketTypeInfoResp?.ticketTypeInfos;

                    const ticketTypeInfo = info.find(info => info.languageId == languageId) ?? info[0];

                    return { ...ticket, ticketType: ticketType, info: ticketTypeInfo }
                })
            )

            setTickets(updatedTickets);
        }
        catch (error) {
            console.error(error);
        }
    }

    const fetchTicketTypeById = async (id) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken token found at fetchTicketTypeById");
            }
            const data = await GetTicketTypeById(baseUrl, "token", regularToken, id);

            return data;
        } catch (error) {
            console.error('Error fetching ticket type by id:', error);
        }
    };

    const fetchtTicketTypeInfosByTicketTypeId = async (ticketTypeId) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken token found at fetchtTicketTypeInfosByTicketTypeId");
            }
            const data = await GetTicketTypeInfosByTicketTypeId(baseUrl, "token", regularToken, ticketTypeId);

            return data;
        } catch (error) {
            console.error('Error fetching ticket type infos by ticket type id:', error);
        }
    };

    const fetchOrderById = async (orderId) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken found at fetchOrderById");
            }
            api.defaults.baseURL = baseUrl;
            const res = await GetShopBasketOrder(baseUrl, "token", regularToken, orderId);
            return res;
        } catch (error) {
            console.error('Error fetching order by Id:', error);
        }
    };

    const handleChangePaymentType = async (paymentMethodId) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken token found at handleChangePaymentType");
            }
            
            const exists = await validateOrderStillExists();
            if (!exists) {
                setSubmitError(resources?.OrderExpiredMessage || "Your reservation is no longer available. Please start again.");
                return;
            }
            
            const data = await ChangePaymentType(baseUrl, "token", regularToken, orderId, paymentMethodId);
            await loadOrder(orderId);

            return data;
        } catch (error) {
            console.error('Error handling change payment type:', error);
        }
    };

    const handleChangeDeliveryType = async (deliveryTypeId) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken token found at handleChangeDeliveryType");
            }
            
            const exists = await validateOrderStillExists();
            if (!exists) {
                setSubmitError(resources?.OrderExpiredMessage || "Your reservation is no longer available. Please start again.");
                return;
            }
            
            const data = await ChangeDeliveryMethod(baseUrl, "token", regularToken, orderId, deliveryTypeId);
            await loadOrder(orderId);

            return data;
        } catch (error) {
            console.error('Error handling change delivery type:', error);
        }
    };


    const startDatatrans = () => {

        let domain = "https://" + window.location.host;

        const redirectUrl = `${domain}/form/redirect/${language}/${orderId}`;
        const errorUrl = `${domain}/form/error/${language}/${orderId}`;
        const cancelUrl = `${domain}/form/cancel/${language}/${orderId}`;

        var baseRequestUrl = `https://datatrans.ticketino.com/Datatrans/${orderId}/DigitalSignature?redirectUrl=${redirectUrl}&errorUrl=${errorUrl}&cancelUrl=${cancelUrl}`;

        axios
            .get(baseRequestUrl)
            .then((res) => {

                var url = res.data.datatransPaymentRedirectUrl.replace("{0}", res.data.transactionId);

                window.location.href = url;
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const validateOrderStillExists = async () => {
        try {
            const latest = await fetchOrderById(orderId);
            if (!latest) return false;
            if (!latest?.id) return false;
            if (!Array.isArray(latest?.tickets) || latest.tickets.length === 0) return false;
            return true;
        } catch (e) {
            return false;
        }
    }

    const onSubmit = async () => {

        setSubmitError("");

        const exists = await validateOrderStillExists();
        if (!exists) {
            setSubmitError(resources?.OrderExpiredMessage || "Your reservation is no longer available. Please start again.");
            return;
        }

        if (order?.totalPrice > 0) {
            let dataTransProviders = [1, 7, 8, 9, 10, 14];

            let datatransProvider = dataTransProviders.findIndex((d) => d === payment.paymentMethodId) !== -1

            if (datatransProvider) {
                startDatatrans();
            } else {
                await confirmShopbasketByOrderId(orderId);
                navigate(`/${language}/confirmation/${orderId}`);
            }
        } else {
            changePaymentTypeToFree();
        }
    }

    const getOrderInsurance = async () => {
        try {
            const res = await axios.get(`${baseUrl}/ShopBasket/Order/${orderId}/Insurance`);
            setOrderInsurancePrice(res.data);
        } catch (error) {
            console.error(error);
        }
    }

    const setOrderInsurance = async () => {
        await axios.put(`${baseUrl}/ShopBasket/Order/${orderId}/Insurance`)
            .then(() => {
                order.reload();
            }).catch((error) => {
                console.error(error);
            })
    }

    const removeOrderInsurance = async () => {
        await axios.delete(`${baseUrl}/ShopBasket/Order/${orderId}/Insurance`)
            .then(() => {
                order.reload();
            }).catch((error) => {
                console.error(error);
            })
    }

    const confirmShopbasketByOrderId = async (orderId) => {
        try {
            const res = await axios.put(`${baseUrl}/ShopBasket/Order/${orderId}/Confirm`);
            return res.data;
        } catch (error) {
            console.error(error)
        }
    };

    const changePaymentTypeToFree = () => {
        axios
            .put(`${baseUrl}/ShopBasket/Order/${orderId}/PaymentType/5`)
            .then(async () => {
                await confirmShopbasketByOrderId(orderId);
                navigate(`/${language}/confirmation/${orderId}`);
            });
    }

    const addPromotionToOrder = async () => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken token found at handleChangeDeliveryType");
            }
            await ApplyPromotionCode(baseUrl, "token", regularToken, orderId, promotioncode)

            setPromotionError({
                text: `${resources.PromocodeSuccess} (${promotioncode})`,
                valid: true
            });

            setAddedPromotionCodes((current) => [...current, promotioncode]);

            await loadOrder(orderId);
        } catch (error) {
            setPromotionError({
                text: resources.PromocodeError,
                valid: false
            });
        }
    }

    const deleteTicketById = async (ticketId) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken token found at deleteTicketById");
            }

            if (orderId && orderId.length > 0) {
                const ticketIdsToRemove = Array.isArray(ticketId) ? ticketId : [ticketId];
                await deleteTicketsFromBasket(baseUrl, "token", regularToken, orderId, ticketIdsToRemove);

                const updatedOrder = await fetchOrderById(orderId);

                // If the only tickets left are the auto-added, non-deletable ones (companion, rally intro),
                // they're meaningless on their own, so remove them too.
                const remainingTickets = updatedOrder?.tickets || [];
                if (remainingTickets.length > 0) {
                    const ticketTypeIdToExternalCode = new Map(
                        tickets.map(t => [t.ticketTypeId, t.ticketType?.externalTicketCode])
                    );
                    const onlyNonDeletableLeft = remainingTickets.every(ticket =>
                        NON_DELETABLE_TICKET_BY_EXTERNALCODES.includes(ticketTypeIdToExternalCode.get(ticket.ticketTypeId))
                    );

                    if (onlyNonDeletableLeft) {
                        await deleteTicketsFromBasket(baseUrl, "token", regularToken, orderId, remainingTickets.map(t => t.id));
                    }
                }

                // Reload the order to get updated tickets
                await loadOrder(orderId);

                // Check if there are no tickets left and navigate to tickets page
                const finalOrder = await fetchOrderById(orderId);
                if (!finalOrder || !finalOrder.tickets || finalOrder.tickets.length === 0) {
                    if (bookingType) {
                        navigate(`/${language}/tickets/${bookingType}`);
                    } else {
                        navigate(`/${language}/tickets-rally`);
                    }
                }
            }
        } catch (error) {
            console.error('Error deleting ticket by id:', error);
        }
    };

    // Determines if invoice payment option is available based on booking type and order details
    // Birthday: invoice never available
    // Groupvisit/Schoolgroup: invoice only available with 12+ tickets AND total >= 200 CHF
    const isInvoiceAvailable = () => {
        return true; // for testing purposes, show invoice option for all booking types and orders

        //uncomment the following all code bellow to apply the actual business logic for showing/hiding the 
        //invoice payment option based on booking type and order details

        //if (bookingType === "birthday") {
        //    return false;
        //}

        //if (bookingType === "groupvisit" || bookingType === "schoolgroup") {
        //    const ticketCount = order?.tickets?.length || 0;
        //    const totalPrice = order?.totalPrice || 0;
        //    return ticketCount > 11 && totalPrice >= 200.00;
        //}

        //return true;
    };

    return (
        <>
            <div id="datatrans-form-placeholder"></div>

            <Header language={language} pagePath="checkout" pageTitle={resources?.PageTitle}
                isLoginVisible={false} isSoppingcartVisible={false} isUsernameVisible={false} />

            <div className="page-container">

                <div className="container wrapper">
                    {/* <div className="row mt-4">
                        <div className="col-md-12 mb-1 text-center">
                            <strong>{resources.DeliveryOption}</strong>
                        </div>
                    </div>
                    <div className="mb-4">
                        <div className="form-check mb-3">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="flexRadioDefault1"
                                checked={payment.deliveryMethodId === 1}
                                onChange={() => handleChangeDeliveryType(1)}
                                id="printAtHome"
                            />
                            <label
                                className="d-inline"
                                htmlFor="printAtHome"
                            >
                                {resources.PrintAtHome}
                                <p className="text-secondary">{resources.PrintAtHomeDescription}</p>
                            </label>
                        </div>
                    </div> */}

                    {order?.totalPrice > 0 &&
                        <>
                            <div className="row mt-5">
                                <div className="form-check mb-3">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="flexRadioDefault"
                                        checked={payment.paymentMethodId === 1}
                                        onChange={async () => {
                                            await handleChangePaymentType(1);
                                        }}
                                        id="mastercard"
                                    />
                                    <label
                                        className="d-inline"
                                        htmlFor="mastercard"
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {resources.MasterCard}
                                        <p className="text-secondary">{resources.MasterCardDesc}</p>
                                    </label>
                                </div>

                                <div className="form-check mb-3 mt-4">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="flexRadioDefault"
                                        checked={payment.paymentMethodId === 7}
                                        onChange={async () => {
                                            await handleChangePaymentType(7);
                                        }}
                                        id="visa"
                                    />
                                    <label
                                        className="d-inline"
                                        htmlFor="visa"
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {resources.Visa}
                                        <p className="text-secondary">{resources.VisaDesc}</p>
                                    </label>
                                </div>

                                <div className="form-check mb-3 mt-4">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="flexRadioDefault"
                                        checked={payment.paymentMethodId === 14}
                                        onChange={async () => {
                                            await handleChangePaymentType(14);
                                        }}
                                        id="twint"
                                    />
                                    <label
                                        className="d-inline"
                                        htmlFor="twint"
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {resources.Twint}
                                        <p className="text-secondary">{resources.TwintDesc}</p>
                                    </label>
                                </div>

                                {isInvoiceAvailable() && (
                                    <div className="form-check mb-3 mt-4">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="flexRadioDefault"
                                            checked={payment.paymentMethodId === 2}
                                            onChange={() => handleChangePaymentType(2)}
                                            id="invoice"
                                        />
                                        <label
                                            className="d-inline"
                                            htmlFor="invoice"
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {resources.PaymmentInvoice}
                                            <p className="text-secondary">{resources.PaymentSmallFee}</p>
                                        </label>
                                    </div>
                                )}

                            </div>
                        </>
                    }

                    {submitError && (
                        <div className="row mt-3">
                            <div className="col-12">
                                <div className="alert alert-danger" role="alert">
                                    {submitError}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* promocode */}
                    <div className="row text-center mt-5">
                        <div className="col-md-12">
                            <span className="title">Promocode</span>
                        </div>
                    </div>

                    <div className="row mt-5">
                        <p>{resources?.PromocodeDescription}</p>

                        <div className="col-7">
                            <input className="form-control" value={promotioncode}
                                onChange={(e) => setPromotioncode(e.target.value)} />
                        </div>
                        <div className="col-4">
                            <button className="btn-custom" onClick={() => addPromotionToOrder()}
                                disabled={promotioncode === ""}>{resources?.Redeem}</button>
                        </div>
                    </div>

                    {promotionError.text && (
                        <div className="row">
                            <div className="col-md-12">
                                {!promotionError.valid ? <p className="text-danger">
                                    {promotionError.text}
                                </p> :
                                    <p className="text-success">
                                        {promotionError.text}
                                    </p>}
                            </div>
                        </div>
                    )}

                    {/* summary */}
                    <div className="row text-center mt-5">
                        <div className="col-md-12">
                            <span className="title">{resources?.Summary}</span>
                        </div>
                    </div>

                    <div className="row mt-5">
                        <div className="col-md-12">
                            <OrderSummary
                                tickets={tickets}
                                order={order}
                                resources={resources}
                                language={language}
                                showDeliveryCosts={true}
                                showPromotionCodes={true}
                                promotionCodes={addedPromotionCodes}
                                onDeleteTicket={deleteTicketById}
                            />
                        </div>
                    </div>

                    {/* AGBs */}
                    <div className="mt-5 custom-box-red">
                        {/* Payment-specific AGB */}
                        <div className="form-check">
                            <input
                                id="agb"
                                className="form-check-input"
                                type="checkbox"
                                checked={isAgbChecked}
                                onChange={e => setIsAgbChecked(e.target.checked)}
                            />
                            <label
                                className="d-inline"
                                htmlFor="agb"
                                style={{ cursor: 'pointer' }}
                            >
                                <div>
                                    {payment.paymentMethodId === 2 ? (
                                        <div dangerouslySetInnerHTML={{ __html: resources?.AGBInvoice }}></div>
                                    ) : (
                                        <div dangerouslySetInnerHTML={{ __html: resources?.AGBEPayment }}></div>
                                    )}
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="custom-box-transparent">
                        {/* General AGB */}
                        <div className="form-check">
                            <input
                                id="agb2"
                                className="form-check-input"
                                type="checkbox"
                                checked={isAgb2Checked}
                                onChange={e => setIsAgb2Checked(e.target.checked)}
                            />
                            <label
                                className="d-inline"
                                htmlFor="agb2"
                                style={{ cursor: 'pointer' }}
                            >
                                <div>
                                    <div className="mt-2" dangerouslySetInnerHTML={{ __html: resources?.AGB1 }}></div>
                                    <div className="mt-2" dangerouslySetInnerHTML={{ __html: resources?.AGB2 }}></div>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* button */}
                    <div className="row mt-5">
                        <div className="col-md-12 text-center">
                            <button
                                className="btn-custom"
                                onClick={() => onSubmit()}
                                disabled={isButtonFinishOrderDisabled}
                            >
                                {resources?.Buy}
                            </button>
                        </div>
                    </div>

                    {submitError && (
                        <div className="row mt-3">
                            <div className="col-12">
                                <div className="alert alert-danger" role="alert">
                                    {submitError}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Footer language={language} />
        </>
    )
}

export default Checkout